import { buildOperationalRetrievalContext } from '../retrieval/retrievalEngine'
import { compressWithCiteTags } from '../retrieval/retrievalCompression'
import type {
  AiRankedRetrievalRecord,
  AiRetrievalRequest,
  AiRetrievalScope,
  AiRetrievalSourceData,
} from '../retrieval/retrievalTypes'
import type { LineCitation, LineCitationSourceKind } from '../citations/citationTypes'
import type { AiMagicChatTurn } from '../../aiService'

export interface LineChatContextInput extends AiRetrievalRequest, AiRetrievalSourceData {
  /**
   * The full thread so far (for user-message self-citation targets). The
   * assembler enumerates USER turns and adds them to the handle dictionary
   * with `sourceKind='user_message'`. Assistant turns are never added.
   */
  thread?: Array<{ id?: string; role: AiMagicChatTurn['role']; text: string }>
}

export interface LineChatContextBundle {
  /** Ready-to-inject CITEABLE SOURCES section for the chat prompt. Empty string when nothing was found. */
  promptBlock: string
  /** Handle (`R-12`) → the resolved record shape the renderer needs to build a full LineCitation. */
  handleToRecord: Map<string, ResolvedCiteRecord>
  /** True when at least one workspace record or user-turn source is cite-able. */
  hasCiteableSources: boolean
}

export interface ResolvedCiteRecord {
  handle: string
  recordId: string
  sourceKind: LineCitationSourceKind
  title: string
  summary: string
  targetRef?: AiRankedRetrievalRecord['targetRef']
}

const SCOPE_TO_KIND: Record<AiRetrievalScope, LineCitationSourceKind> = {
  timeline: 'timeline',
  checklist_graph: 'checklist',
  meetings: 'meeting',
  uploads: 'upload',
  governance_activity: 'governance',
  reports: 'report',
  automations: 'automation',
  stock: 'stock',
  budget: 'budget',
  chat_proposals: 'chat_proposal',
  workspace_pulse: 'pulse',
  cognitive_memory: 'memory',
}

const kindForRecord = (record: AiRankedRetrievalRecord): LineCitationSourceKind => (
  SCOPE_TO_KIND[record.scope] ?? 'other'
)

/**
 * Shared assembler used by the Line chat orchestrator. Produces the
 * CITEABLE SOURCES prompt block, a handle→record lookup for post-parse
 * resolution, and (opt-in) user-message self-cite handles for the same
 * thread. Never throws — retrieval failures degrade gracefully to
 * "no cite-able sources".
 */
export const assembleLineChatContext = (input: LineChatContextInput): LineChatContextBundle => {
  let workspaceSlices: ReturnType<typeof compressWithCiteTags>
  let rankedRecords: AiRankedRetrievalRecord[]
  try {
    const bundle = buildOperationalRetrievalContext({
      ...input,
      limits: input.limits ?? { maxRecords: 20, maxSlices: 8, maxCharacters: 4_800 },
    })
    rankedRecords = bundle.rankedRecords
    workspaceSlices = compressWithCiteTags(rankedRecords, {
      maxSlices: input.limits?.maxSlices ?? 8,
      maxCharacters: input.limits?.maxCharacters ?? 4_800,
    })
  } catch {
    // Retrieval is best-effort. If any provider throws, degrade to no citations.
    return { promptBlock: '', handleToRecord: new Map(), hasCiteableSources: false }
  }

  const handleToRecord = new Map<string, ResolvedCiteRecord>()
  Object.entries(workspaceSlices.handleToRecordId).forEach(([handle, recordId]) => {
    const record = rankedRecords.find((entry) => entry.id === recordId)
    if (!record) return
    handleToRecord.set(handle, {
      handle,
      recordId,
      sourceKind: kindForRecord(record),
      title: record.title,
      summary: record.summary,
      targetRef: record.targetRef,
    })
  })

  // Self-citation handles for previous USER turns (never assistant turns).
  const userTurns = (input.thread ?? []).filter((turn) => turn.role === 'USER' && turn.text.trim())
  const baseIndex = handleToRecord.size
  userTurns.forEach((turn, index) => {
    const handle = `R-U${index + 1}`
    const turnId = turn.id ?? `turn_${index}`
    handleToRecord.set(handle, {
      handle,
      recordId: `user_turn:${turnId}`,
      sourceKind: 'user_message',
      title: `You said (turn ${index + 1})`,
      summary: turn.text.length > 160 ? `${turn.text.slice(0, 157)}…` : turn.text,
    })
    // Ensure sequential handle numbering across workspace + user cites — the
    // slice concat below uses this baseIndex to build the second prompt section.
    void baseIndex
  })

  if (!workspaceSlices.slices.length && !userTurns.length) {
    return { promptBlock: '', handleToRecord, hasCiteableSources: false }
  }

  const workspaceSection = workspaceSlices.slices.length
    ? workspaceSlices.slices.map((slice) => `[${slice.title}]\n${slice.text}`).join('\n\n')
    : ''

  const userSection = userTurns.length
    ? userTurns.map((turn, index) => {
        const preview = turn.text.length > 240 ? `${turn.text.slice(0, 237)}…` : turn.text
        return `- [R-U${index + 1}] User turn ${index + 1}: ${preview}`
      }).join('\n')
    : ''

  const promptBlock = [
    workspaceSection ? `WORKSPACE SOURCES (cite with the matching [R-N] handle):\n${workspaceSection}` : '',
    userSection ? `USER TURN SOURCES (cite with the matching [R-UN] handle):\n${userSection}` : '',
  ].filter(Boolean).join('\n\n')

  return {
    promptBlock,
    handleToRecord,
    hasCiteableSources: handleToRecord.size > 0,
  }
}

/**
 * Resolve draft citations (from the model JSON output) into full
 * LineCitation records using the handle dictionary produced by
 * assembleLineChatContext. Silently drops unresolvable handles.
 */
export const resolveLineCitations = (
  drafts: Array<{ handle?: string; excerpt?: string; confidence?: 'high' | 'inferred' } | undefined | null>,
  handleToRecord: Map<string, ResolvedCiteRecord>,
): LineCitation[] => {
  const resolved: LineCitation[] = []
  const seen = new Set<string>()
  drafts.forEach((draft, idx) => {
    if (!draft?.handle) return
    const record = handleToRecord.get(draft.handle)
    if (!record) return
    if (seen.has(record.recordId)) return
    seen.add(record.recordId)
    resolved.push({
      id: `c${idx + 1}`,
      recordId: record.recordId,
      targetRef: record.targetRef,
      sourceKind: record.sourceKind,
      title: record.title,
      excerpt: (draft.excerpt && draft.excerpt.trim()) || record.summary || record.title,
      confidence: draft.confidence === 'inferred' ? 'inferred' : 'high',
    })
  })
  return resolved
}

/**
 * Replace [R-N] and [R-UN] tokens embedded in the model message with the
 * user-facing 1-based citation index the renderer wants. Unresolved tokens
 * are stripped so the text never leaks the handle back to the user.
 */
export const rewriteMessageWithCitationIndices = (
  message: string,
  citations: LineCitation[],
  handleToRecord: Map<string, ResolvedCiteRecord>,
): string => {
  if (!message) return message
  const handleToCitationIndex = new Map<string, number>()
  citations.forEach((cite, idx) => {
    const handle = [...handleToRecord.entries()].find(([, record]) => record.recordId === cite.recordId)?.[0]
    if (handle) handleToCitationIndex.set(handle, idx + 1)
  })
  return message.replace(/\[R-U?\d+\]/g, (match) => {
    const handle = match.slice(1, -1)
    const idx = handleToCitationIndex.get(handle)
    return idx ? `[[cite:${idx}]]` : ''
  })
}
